"use client";
// @ts-strict-ignore
import React, { useEffect, useState } from "react";
import {
  MobileSearchBar,
  MobileSearchBarContainer,
  MobileSearchContainer,
  MobileSearchSuggestion,
  MobileSearchSuggestions,
  SearchCancelButton
} from "app/_components/layout/Header/CryptopediaHeader/style";
import Downshift from "downshift";
import { AnimatePresence } from "framer-motion";
import { useRouter } from "i18n/routing";
import { connectSearchBox, connectHits } from "react-instantsearch-dom";
import { IconSearch } from "@hubble/icons";
import useDebounce from "hooks/useDebounce";

const SearchHits = connectHits(
  ({
    hits,
    getInputProps,
    getItemProps,
    getMenuProps,
    getRootProps,
    highlightedIndex,
    handleSuggestionClick,
    handleSubmit
  }) => {
    return (
      <form
        onSubmit={(...args) => {
          args[0].preventDefault();
          handleSubmit?.(...args);
        }}
        {...getRootProps({ refKey: undefined }, { suppressRefError: true })}
      >
        <MobileSearchBarContainer>
          <IconSearch />
          <MobileSearchBar
            type="text"
            placeholder="Search Cryptopedia"
            {...getInputProps()}
            autoFocus
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              const target = e.target as HTMLInputElement;
              if (e.key === "Enter" && target.value) {
                handleSuggestionClick?.({ name: target.value });
              }
            }}
          />
          <SearchCancelButton onClick={handleSuggestionClick}>
            Cancel
          </SearchCancelButton>
        </MobileSearchBarContainer>
        <MobileSearchSuggestions {...getMenuProps()}>
          {hits.map((item: { id: any; name: any }, index: any) => (
            <MobileSearchSuggestion
              {...getItemProps({
                key: item.id,
                index,
                item
              })}
              as="div"
              highlighted={highlightedIndex === index}
            >
              {item.name}
            </MobileSearchSuggestion>
          ))}
        </MobileSearchSuggestions>
      </form>
    );
  }
);

const MobileSearch = ({
  currentRefinement,
  refine,
  isOpen = false,
  onClose
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [, cancel] = useDebounce(
    () => {
      refine(searchTerm);
      setShowSuggestions(true);
    },
    200,
    [searchTerm, refine]
  );
  const router = useRouter();

  const handleSearch = (term: any) => {
    if (!term) {
      onClose?.();
      return;
    }
    const url = `/cryptopedia/search?query=${term}`;
    router.push(url);
  };

  const handleSuggestionClick = ({ name }) => {
    setShowSuggestions(false);
    handleSearch(name);
  };

  const handleChange = (e: React.SetStateAction<string>) => {
    setSearchTerm(e);
  };

  useEffect(() => {
    if (!currentRefinement) {
      setShowSuggestions(false);
    }
  }, [currentRefinement]);

  return (
    <AnimatePresence>
      {isOpen && (
        <MobileSearchContainer
          initial={{
            scale: 0.3,
            opacity: 0
          }}
          animate={{
            scale: 1,
            opacity: 1
          }}
          exit={{
            scale: 0.3,
            opacity: 0
          }}
          transition={{
            easing: "easeInOut",
            duration: 0.3
          }}
        >
          <Downshift
            onChange={handleSuggestionClick}
            onInputValueChange={handleChange}
            onOuterClick={cancel}
            itemToString={(item) => (item ? item.name : "")}
          >
            {(props) => (
              <SearchHits
                setShowSuggestions={setShowSuggestions}
                {...props.getRootProps(
                  { refKey: null },
                  { suppressRefError: true }
                )}
                handleSuggestionClick={handleSuggestionClick}
                active={showSuggestions}
                {...props}
                isOpen={isOpen}
              />
            )}
          </Downshift>
        </MobileSearchContainer>
      )}
    </AnimatePresence>
  );
};

export default connectSearchBox(MobileSearch);
